import { Card, Title, Text } from "@tremor/react";
import {
  Globe, AlertCircle, ScrollText, FileText, ArrowRight, Clock, CheckCircle2, AlertTriangle,
  Loader2, Sparkles,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { useDashboardSummary, type Audit, type Finding } from "@/lib/queries";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";

const SEV_DOT: Record<string, string> = {
  critical: "bg-red-500",
  high:     "bg-orange-500",
  medium:   "bg-amber-500",
  low:      "bg-emerald-500",
  info:     "bg-blue-500",
};

interface KpiProps {
  label: string;
  value: string;
  icon: LucideIcon;
  loading?: boolean;
  highlight?: boolean;
  hint?: string;
}

function Kpi({ label, value, icon: Icon, loading, highlight, hint }: KpiProps) {
  return (
    <Card className={cn(highlight && "border-red-500/40 ring-1 ring-red-500/20")}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs text-muted-foreground">{label}</div>
          <div className="mt-1 text-3xl font-semibold tracking-tight tabular-nums">
            {loading ? <span className="inline-block h-8 w-16 animate-pulse rounded bg-muted" /> : value}
          </div>
          {hint && <div className="mt-1 text-[11px] text-muted-foreground">{hint}</div>}
        </div>
        <div className={cn(
          "flex h-9 w-9 items-center justify-center rounded-lg",
          highlight ? "bg-red-500/15" : "bg-primary/10",
        )}>
          <Icon className={cn("h-4 w-4", highlight ? "text-red-600" : "text-primary")} />
        </div>
      </div>
    </Card>
  );
}

function fmtTime(iso: string): string {
  const dt = (Date.now() - new Date(iso).getTime()) / 1000;
  if (dt < 60)        return "just now";
  if (dt < 3600)      return `${Math.floor(dt / 60)}m ago`;
  if (dt < 86_400)    return `${Math.floor(dt / 3600)}h ago`;
  return `${Math.floor(dt / 86_400)}d ago`;
}

export default function Dashboard() {
  const summary = useDashboardSummary();
  const { user } = useAuth();
  const k = summary.data?.kpis;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">
          Welcome{user?.full_name ? `, ${user.full_name.split(" ")[0]}` : ""}.
        </h1>
        <p className="text-sm text-muted-foreground">
          Auriel's view of {k?.properties_active ?? 0} active propert
          {(k?.properties_active ?? 0) === 1 ? "y" : "ies"} —
          findings, audits, reports, ranking signals.
        </p>
      </div>

      {/* Needs attention strip */}
      {(k?.findings_critical ?? 0) > 0 && (
        <Card className="border-red-500/40 bg-red-500/5">
          <div className="flex items-start gap-3">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
            <div>
              <h3 className="text-sm font-semibold text-red-700 dark:text-red-400">
                {k?.findings_critical} critical finding{k?.findings_critical === 1 ? "" : "s"} need attention
              </h3>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Plus {k?.findings_high ?? 0} high-severity. <Link to="/findings" className="text-primary hover:underline">View findings →</Link>
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* KPI tiles */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
        <Kpi label="Properties"   value={String(k?.properties_active ?? 0)}     icon={Globe}      loading={summary.isLoading} />
        <Kpi label="Findings open" value={String(k?.findings_open ?? 0)}        icon={AlertCircle} loading={summary.isLoading}
             highlight={(k?.findings_critical ?? 0) > 0}
             hint={(k?.findings_critical ?? 0) > 0 ? `${k!.findings_critical} critical` : undefined} />
        <Kpi label="Critical"     value={String(k?.findings_critical ?? 0)}    icon={AlertCircle} loading={summary.isLoading} highlight={(k?.findings_critical ?? 0) > 0} />
        <Kpi label="High"         value={String(k?.findings_high ?? 0)}        icon={AlertCircle} loading={summary.isLoading} />
        <Kpi label="Audits (7d)"  value={String(k?.audits_7d ?? 0)}            icon={ScrollText}  loading={summary.isLoading} />
        <Kpi label="Reports (30d)" value={String(k?.reports_30d ?? 0)}         icon={FileText}    loading={summary.isLoading} />
      </div>

      {/* Two-column: top open findings + recent audits */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card>
          <div className="flex items-center justify-between">
            <Title>Top open findings</Title>
            <Link to="/findings" className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
              all findings <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          {summary.isLoading && <p className="mt-3 text-sm text-muted-foreground">Loading…</p>}
          {(summary.data?.top_open_findings ?? []).length === 0 && !summary.isLoading && (
            <div className="mt-3 rounded-md border border-emerald-500/30 bg-emerald-500/5 px-3 py-2 text-sm text-emerald-700 dark:text-emerald-400">
              All clear — no open findings right now.
            </div>
          )}
          <div className="mt-3 space-y-2">
            {summary.data?.top_open_findings.map((f: Finding) => (
              <Link
                key={f.id}
                to={`/findings`}
                className="block rounded-md border border-border bg-card px-3 py-2 transition-colors hover:bg-accent/40"
              >
                <div className="flex items-start gap-2">
                  <span className={cn("mt-1.5 h-2 w-2 shrink-0 rounded-full", SEV_DOT[f.severity])} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">{f.title}</p>
                    <p className="mt-0.5 text-[11px] text-muted-foreground">
                      {f.property_domain && (
                        <span className="text-foreground/80">{f.property_domain} · </span>
                      )}
                      <span className="font-mono">{f.kind}</span> · {fmtTime(f.detected_at)}
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between">
            <Title>Recent audits</Title>
            <Link to="/audits" className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
              all audits <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          {summary.isLoading && <p className="mt-3 text-sm text-muted-foreground">Loading…</p>}
          {(summary.data?.recent_audits ?? []).length === 0 && !summary.isLoading && (
            <p className="mt-3 text-sm text-muted-foreground">
              No audits yet. <Link to="/properties" className="text-primary hover:underline">Add a property</Link> and run one.
            </p>
          )}
          <div className="mt-3 space-y-2">
            {summary.data?.recent_audits.map((a: Audit) => {
              const findingsCount = (a.metadata as { findings_count?: number })?.findings_count ?? 0;
              const status = a.status; // server-derived; see _derive_audit_status
              return (
                <Link
                  key={a.id}
                  to={`/audits`}
                  className="block rounded-md border border-border bg-card px-3 py-2 transition-colors hover:bg-accent/40"
                >
                  <div className="flex items-start gap-2">
                    {status === "ok" && <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-500" />}
                    {status === "error" && <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-500" />}
                    {status === "running" && <Loader2 className="mt-0.5 h-3.5 w-3.5 shrink-0 animate-spin text-primary" />}
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{a.display_prompt ?? `Audit #${a.id}`}</p>
                      <p className="mt-0.5 text-[11px] text-muted-foreground">
                        <Clock className="inline h-3 w-3 align-baseline" /> {fmtTime(a.run_at)}
                        {a.duration_s !== null && <> · {a.duration_s.toFixed(0)}s</>}
                        {findingsCount > 0 && <> · {findingsCount} finding{findingsCount === 1 ? "" : "s"}</>}
                      </p>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </Card>
      </div>

      <Card>
        <div className="flex items-start gap-3">
          <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
          <div>
            <Title>Talk to Auriel</Title>
            <Text className="mt-1">
              Use the chat panel (top-right) for ad-hoc questions:
              "show me the rank trend for nws.ro this week",
              "what's broken on nrg.ro",
              "what should we do about that critical finding".
            </Text>
          </div>
        </div>
      </Card>
    </div>
  );
}
